import { useCallback, useRef, useState } from 'react';
import { P2GBox } from '@parcel2go/core-ui';
import { useEnsureLiveSession } from '@parcel2go/npm-p2g-app';
import SubscriptionCheckoutFrame, {
  SubscriptionCheckoutTarget,
  SubscriptionCompleteResult
} from './subscription-checkout-frame';
import PlanPicker from './plan-picker';
import { findPlanCard, usePlanCards } from './use-plan-cards';
import {
  isPendingLookupKey,
  PendingSubscriptionPlan,
  setPendingSubscriptionPlan
} from '~/hooks/use-pending-subscription-plan';
import useSignIn from '~/hooks/use-signin';
import { useEntitlements } from '~/hooks/use-entitlements';
import { markEntitlementsSyncNeeded } from './pending-entitlements-sync';
import { captureWarning } from '~/lib/metrics';
import { SubscriptionInterval, SubscriptionTier } from './types';

export interface SubscribeOutcome {
  subscriptionId?: string;
  tier: SubscriptionTier | null;
  interval: SubscriptionInterval | null;
}

interface props {
  plan: PendingSubscriptionPlan;
  title: string;
  onDone: (outcome: SubscribeOutcome) => void;
}

/**
 * Set immediately before a sign-in redirect and cleared when the checkout settles. A round trip
 * that comes back still unauthenticated would otherwise redirect forever, which is the one outcome
 * worse than leaving the frame's own sign-in panel on screen.
 */
const AUTH_REDIRECT_MARKER = 'SUBSCRIPTION-AUTH-REDIRECT-ATTEMPTED';

/** A blocked or full sessionStorage must never stop a completed subscription being reported. */
function clearAuthRedirectMarker(): void {
  try {
    window.sessionStorage.removeItem(AUTH_REDIRECT_MARKER);
  } catch {
    // Nothing to clear if storage is unavailable - the marker was never written either.
  }
}

/**
 * Stamps the plan onto the current URL, so the capture hook at the app root picks it back up.
 *
 * The complementary params are deleted rather than left in place: the two spellings are
 * alternatives, and a URL carrying both would leave which one wins to the capture hook's precedence
 * rules instead of saying plainly which plan this is.
 */
function buildReturnUrl(plan: PendingSubscriptionPlan): string {
  const url = new URL(window.location.href);
  url.searchParams.delete('plan');
  url.searchParams.delete('tier');
  url.searchParams.delete('interval');

  if ('plan' in plan && plan.plan) {
    url.searchParams.set('plan', plan.plan);
  } else if (plan.tier && plan.interval) {
    url.searchParams.set('tier', plan.tier);
    url.searchParams.set('interval', plan.interval);
  }
  return url.toString();
}

/**
 * The shared "subscribe to a specific tier" surface - used by both the onboarding wizard's first
 * step (a not-yet-onboarded customer) and My Plan (an already-onboarded customer redirected there).
 * Defaults to the requested tier/interval (or lookup key); "Choose a different plan" swaps in a
 * lightweight tier-card picker (real pricing, from the anonymous subscription-plans endpoint) rather
 * than redirecting anywhere, per ui-checkout's own documented pattern for
 * `subscription:change-plan` - the frame cannot retarget itself, so the host reopens its own picker.
 * The picker REPLACES the frame while it is up, so choosing a card mounts a fresh one at the new
 * target; `reloadNonce` is the separate mechanism for re-loading a frame in place, which matters
 * because a remount invalidates the listener's `contentWindow`.
 *
 * It also owns the recovery policy for `subscription:auth-required`, since it is the component that
 * knows which plan the customer was choosing.
 */
const SubscribePanel = ({ plan, title, onDone }: props) => {
  const [target, setTarget] = useState<SubscriptionCheckoutTarget>(plan);
  const [showPicker, setShowPicker] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  // How far recovery has already gone for this mount. Each step is tried AT MOST once: a step that
  // did not resolve the frame's complaint cannot resolve it on a second identical attempt, and
  // re-running one in response to the message it failed to silence is an unbounded loop.
  const recoveryStageRef = useRef<'idle' | 'renewed' | 'redirected'>('idle');
  const recoveringRef = useRef(false);
  const ensureLiveSession = useEnsureLiveSession();
  // Deduped by SWR with the picker's own read, so this costs no extra request.
  const { cards } = usePlanCards();
  const { onSignInClick } = useSignIn({ location: 'subscriptions', section: 'subscriptions' });
  const { sync } = useEntitlements();

  const settle = useCallback(
    (outcome: SubscribeOutcome) => {
      clearAuthRedirectMarker();
      // Only for an actual subscribe - a skip changed nothing worth re-reading. sync() corrects
      // whichever useEntitlements() is already mounted; the flag covers one that mounts later (see
      // pending-entitlements-sync.ts).
      if (outcome.subscriptionId) {
        void sync();
        markEntitlementsSyncNeeded();
      }
      onDone(outcome);
    },
    [onDone, sync]
  );

  const handleComplete = useCallback(
    (result: SubscriptionCompleteResult) => {
      settle({ subscriptionId: result.subscriptionId, tier: result.tier, interval: result.interval });
    },
    [settle]
  );

  const handleSkip = useCallback(() => {
    settle({ tier: null, interval: null });
  }, [settle]);

  const handleChangePlan = useCallback(() => {
    setShowPicker(true);
  }, []);

  const handleSelectPlan = useCallback((tier: SubscriptionTier, interval: SubscriptionInterval) => {
    setTarget({ tier, interval });
    setShowPicker(false);
  }, []);

  const handleAuthRequired = useCallback(
    async (lookupKey: string | null) => {
      // The frame re-announces on every reload, so ignore anything arriving mid-recovery.
      if (recoveringRef.current) {
        return;
      }
      recoveringRef.current = true;

      try {
        // The key rides in on a postMessage, so it is checked against the same rule every other
        // writer uses. An unusable one falls back to what this panel is already aimed at: storing it
        // regardless would have the reader refuse it later and lose the plan silently, which is the
        // one outcome worse than not honouring the message's own suggestion.
        const returnPlan: PendingSubscriptionPlan = isPendingLookupKey(lookupKey) ? { plan: lookupKey } : target;

        // Cheapest first. My Account renders behind `isAuthenticated`, which stays true on an expired
        // session, so this message is often a lapsed token - and a bounded silent renewal fixes that
        // without navigating away or restarting the onboarding wizard the customer is halfway
        // through.
        if (recoveryStageRef.current === 'idle') {
          const user = await ensureLiveSession({ section: 'subscriptions' });
          // Marked tried whatever the outcome. A renewal renews OUR session, which is not always
          // what the frame is missing - a cross-origin frame has its own, and no amount of renewing
          // here will give it one. If the frame announces again after this, the renewal was not the
          // answer, so the next message escalates rather than repeating it.
          recoveryStageRef.current = 'renewed';
          if (user && !user.expired) {
            setReloadNonce((nonce) => nonce + 1);
            return;
          }
        }

        if (recoveryStageRef.current === 'redirected') {
          captureWarning('subscription:auth-required persisted after a sign-in attempt', {
            section: 'subscribe-panel'
          });
          return;
        }

        let alreadyTried = false;
        try {
          alreadyTried = window.sessionStorage.getItem(AUTH_REDIRECT_MARKER) === 'true';
          window.sessionStorage.setItem(AUTH_REDIRECT_MARKER, 'true');
        } catch {
          // No storage means no loop guard across the round trip, so don't redirect on a guess -
          // leave the frame's own sign-in panel visible instead.
          alreadyTried = true;
        }

        if (alreadyTried) {
          captureWarning('subscription:auth-required persisted after a sign-in attempt', {
            section: 'subscribe-panel'
          });
          return;
        }

        recoveryStageRef.current = 'redirected';

        // Belt and braces: sessionStorage survives the round trip in this tab, and the query params
        // are re-read by usePendingSubscriptionPlanCapture at the app root if storage was cleared.
        setPendingSubscriptionPlan(returnPlan);
        onSignInClick(buildReturnUrl(returnPlan));
      } finally {
        recoveringRef.current = false;
      }
    },
    [target, ensureLiveSession, onSignInClick]
  );

  if (showPicker) {
    // A lookup-key target names an interval too - ask the catalogue which price it is rather than
    // defaulting to monthly and showing someone who chose annual the wrong prices first.
    const currentInterval: SubscriptionInterval = target.interval ?? findPlanCard(cards, target)?.interval ?? 'month';
    return (
      <P2GBox>
        <PlanPicker interval={currentInterval} onSelect={handleSelectPlan} />
      </P2GBox>
    );
  }

  return (
    <SubscriptionCheckoutFrame
      target={target}
      title={title}
      reloadNonce={reloadNonce}
      onComplete={handleComplete}
      onSkip={handleSkip}
      onChangePlan={handleChangePlan}
      onAuthRequired={handleAuthRequired}
      // Onboarding renders its own way out of this step - the wizard footer's "Skip, I'll do this
      // later" - so the frame's own "Skip - continue on Free plan" is the same offer a second time,
      // and taking it would leave the wizard's progress bar standing over a finished flow. Both of
      // the frame's secondary actions go together: see `hideSecondaryActions`.
      hideSecondaryActions
    />
  );
};

export default SubscribePanel;
